import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { Card, CardBody, Skeleton, type ScorecardData } from '@structural-climbing/ui'
import type { CompetitionCategory } from './types'

const PODIUM = [
  'bg-[#f5d37a] text-[#5c3d00]',
  'bg-[#dcdcdc] text-[#3f3f3f]',
  'bg-[#e8b98e] text-[#5a2e0a]',
]

/**
 * The podium of every category, once the competition is over.
 *
 * Read from the same scorecard the Running stage shows, one category at a time,
 * and only when this tab opens: an ended competition does not change, so one
 * read per category per visit is the whole cost.
 */
export function FinalStandingsTab({
  competitionId,
  categories,
  competitorCount,
  routeCount,
}: {
  competitionId: string
  categories: CompetitionCategory[]
  competitorCount: number
  routeCount: number
}) {
  const { t, i18n } = useTranslation()
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string
  const [boards, setBoards] = useState<Record<string, ScorecardData | null>>({})
  const [loading, setLoading] = useState(true)
  const number = new Intl.NumberFormat(i18n.language.startsWith('ro') ? 'ro-RO' : 'en-GB', {
    maximumFractionDigits: 2,
  })

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      try {
        const token = await getAccessTokenSilently()
        const pairs = await Promise.all(
          categories.map(async (category) => {
            const res = await fetch(
              `${apiUrl}/competitions/${competitionId}/scorecard?category=${encodeURIComponent(category.id)}`,
              { headers: { Authorization: `Bearer ${token}` } },
            )
            return [category.id, res.ok ? ((await res.json()) as ScorecardData) : null] as const
          }),
        )
        if (!cancelled) setBoards(Object.fromEntries(pairs))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [apiUrl, competitionId, categories, getAccessTokenSilently])

  const stats = [
    { label: t('competitionSetup.statCompetitors'), value: competitorCount },
    { label: t('competitionSetup.statCategories'), value: categories.length },
    { label: t('competitionSetup.statRoutes'), value: routeCount },
  ]

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-3">
        {stats.map((s) => (
          <Card key={s.label} shadow="none" className="border border-divider">
            <CardBody className="gap-1">
              <p className="text-sm text-foreground/60">{s.label}</p>
              <p className="text-2xl font-bold tabular-nums">{s.value}</p>
            </CardBody>
          </Card>
        ))}
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {categories.map((category) => {
          const board = boards[category.id]
          const top = (board?.climbers ?? []).filter((c) => c.totalScore > 0).slice(0, 3)
          return (
            <Card key={category.id} shadow="none" className="border border-divider">
              <CardBody className="gap-3">
                <p className="font-bold">{category.name}</p>
                {loading ? (
                  <Skeleton className="h-24 rounded-xl" />
                ) : top.length === 0 ? (
                  <p className="text-sm text-foreground/60">{t('competitionSetup.finalEmpty')}</p>
                ) : (
                  <ol className="flex flex-col gap-2">
                    {top.map((climber, i) => (
                      <li key={climber.userId} className="flex items-center gap-3 text-sm">
                        <span
                          className={`grid size-7 shrink-0 place-items-center rounded-full text-xs font-extrabold ${PODIUM[Math.min(climber.rank, 3) - 1] ?? PODIUM[i]}`}
                        >
                          {climber.rank}
                        </span>
                        <span className="min-w-0 flex-1 truncate">
                          {climber.fullName || climber.displayName}
                        </span>
                        <span className="font-semibold tabular-nums">
                          {t('competitionSetup.pointsShort', {
                            points: number.format(climber.totalScore),
                          })}
                        </span>
                      </li>
                    ))}
                  </ol>
                )}
              </CardBody>
            </Card>
          )
        })}
      </div>
      <p className="text-xs text-foreground/60">{t('competitions.endedLockNote')}</p>
    </div>
  )
}
